import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { MagazineService } from 'app/services/magazine/magazine.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';

@Component({
  selector: 'app-ajouter-magazine',
  templateUrl: './ajouter-magazine.component.html',
  styleUrls: ['./ajouter-magazine.component.scss']
})
export class AjouterMagazineComponent implements OnInit, OnDestroy {
  // Form
  magazineForm!: FormGroup;
  
  // Image
  imagePreview: string | ArrayBuffer | null = null;
  imageFile: File | null = null;
  uploadedImageUrl: string | null = null;
  
  // PDF - Upload Mode
  pdfFile: File | null = null;
  pdfFileName: string | null = null;
  isUploadingPdf: boolean = false;
  uploadedPdfUrl: string | null = null;
  
  // PDF - URL Mode
  pdfInputMode: 'upload' | 'url' = 'upload'; // Default to upload
  
  // States
  isUploading: boolean = false;
  isSubmitting: boolean = false;
  hasUnsavedChanges: boolean = false;
  
  // Data
  magazineListe: any[] = [];
  magazinesCount: number = 0;

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private magazineService: MagazineService,
    private awsService: AwscontrolService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadMagazinesCount();
    
    // Détecter les changements non sauvegardés
    this.magazineForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.hasUnsavedChanges = true;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.magazineForm = this.fb.group({
      name: ['', [
        Validators.required, 
        Validators.minLength(3), 
        Validators.maxLength(200)
      ]],
      nbr_telechargement: [0, [
        Validators.required, 
        Validators.min(0), 
        Validators.max(1000000)
      ]],
      downloadCount: [0, [
        Validators.required,
        Validators.min(0),
        Validators.max(1000000)
      ]],
      // Added control for manual URL input
      pdfManualUrl: [''] 
    });
  }

  // Getter sécurisé pour les contrôles du formulaire
  get formControls(): { [key: string]: AbstractControl } {
    return this.magazineForm?.controls || {};
  }

  get nameControl(): AbstractControl | null {
    return this.magazineForm?.get('name');
  }

  get nbrTelechargementControl(): AbstractControl | null {
    return this.magazineForm?.get('nbr_telechargement');
  }

  get downloadCountControl(): AbstractControl | null {
    return this.magazineForm?.get('downloadCount');
  }
  
  get pdfManualUrlControl(): AbstractControl | null {
    return this.magazineForm?.get('pdfManualUrl');
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.magazineForm?.get(controlName);
    return !!(control?.invalid && control?.touched);
  }

  getControlErrors(controlName: string): any {
    const control = this.magazineForm?.get(controlName);
    return control?.errors || {};
  }

  private loadMagazinesCount(): void {
    this.magazineService.getListMagazine()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.magazineListe = Array.isArray(data) ? data : [data];
          this.magazinesCount = this.magazineListe.length;
        },
        error: (error) => {
          console.error('Error loading magazines:', error);
        }
      });
  }

  // ========== MODE SWITCHING ==========
  switchPdfMode(mode: 'upload' | 'url'): void {
    if (this.pdfInputMode === mode) return;
    
    this.pdfInputMode = mode;
    this.hasUnsavedChanges = true;

    // Clear previous data when switching to avoid confusion
    if (mode === 'url') {
      this.pdfFile = null;
      this.pdfFileName = null;
      this.uploadedPdfUrl = null;
    } else {
      // Clear URL input when switching back to upload
      this.pdfManualUrlControl?.setValue('');
      this.pdfManualUrlControl?.markAsPristine();
      this.pdfManualUrlControl?.markAsUntouched();
    }
  }

  // ========== IMAGE UPLOAD ==========
  selectFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        this.showError('Type de fichier non supporté', 'Veuillez sélectionner une image (JPEG, PNG, GIF, WebP)');
        return;
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        this.showError('Fichier trop volumineux', 'La taille maximale est de 5MB');
        return;
      }

      this.imageFile = file;
      this.previewImage(file);
      this.hasUnsavedChanges = true;
    }
  }

  private previewImage(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.imagePreview = e.target?.result || null;
    };
    reader.onerror = () => {
      this.showError('Erreur de lecture', 'Impossible de lire le fichier image');
      this.imagePreview = null;
    };
    reader.readAsDataURL(file);
  }

  async uploadImageToAws(): Promise<string | null> {
    if (!this.imageFile) return null;

    this.isUploading = true;
    
    try {
      Swal.fire({
        title: 'Téléchargement en cours...',
        html: 'Upload de l\'image vers le serveur',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading()
      });

      const folder = 'magazines';
      const response = await this.awsService.uploadFile(this.imageFile, folder).toPromise();
      
      Swal.close();
      
      if (response?.success && response.urls && response.urls.length > 0) {
        this.uploadedImageUrl = response.urls[0];
        this.isUploading = false;
        return response.urls[0];
      } else {
        throw new Error(response?.error || 'Upload failed');
      }
      
    } catch (error: any) {
      this.isUploading = false;
      Swal.close();
      console.error('AWS Upload Error:', error);
      
      let errorMessage = 'Échec du téléchargement de l\'image';
      if (error.message?.includes('timeout')) {
        errorMessage = 'Délai d\'attente dépassé. Veuillez réessayer.';
      } else if (error.status === 0) {
        errorMessage = 'Impossible de se connecter au serveur. Vérifiez votre connexion.';
      } else if (error.status === 413) {
        errorMessage = 'Fichier trop volumineux pour le serveur';
      }
      
      this.showError('Erreur de téléchargement', errorMessage);
      return null;
    }
  }

  removeImage(): void {
    Swal.fire({
      title: 'Supprimer l\'image ?',
      text: 'Cette action ne peut pas être annulée',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.imagePreview = null;
        this.imageFile = null;
        this.uploadedImageUrl = null;
        this.hasUnsavedChanges = true;
      }
    });
  }

  // ========== PDF UPLOAD ==========
  selectPdfFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        this.showError('Type de fichier non supporté', 'Veuillez sélectionner un fichier PDF');
        return;
      }

      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        this.showError('Fichier trop volumineux', 'La taille maximale est de 50MB');
        return;
      }

      this.pdfFile = file;
      this.pdfFileName = file.name;
      this.hasUnsavedChanges = true;
    }
  }

  async uploadPdfToAws(): Promise<string | null> {
    if (!this.pdfFile) return null;

    this.isUploadingPdf = true;
    
    try {
      Swal.fire({
        title: 'Téléchargement PDF en cours...',
        html: 'Upload du fichier vers le serveur',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading()
      });

      const response = await this.awsService.uploadPdf([this.pdfFile]).toPromise();
      
      Swal.close();
      
      if (response?.success && response.urls && response.urls.length > 0) {
        this.uploadedPdfUrl = response.urls[0];
        this.isUploadingPdf = false;
        return response.urls[0];
      } else {
        throw new Error(response?.errors?.join(', ') || 'Upload failed');
      }
      
    } catch (error: any) {
      this.isUploadingPdf = false;
      Swal.close();
      console.error('AWS PDF Upload Error:', error);
      
      let errorMessage = 'Échec du téléchargement du PDF';
      if (error.message?.includes('timeout')) {
        errorMessage = 'Délai d\'attente dépassé. Veuillez réessayer.';
      } else if (error.status === 0) {
        errorMessage = 'Impossible de se connecter au serveur. Vérifiez votre connexion.';
      }
      
      this.showError('Erreur de téléchargement', errorMessage);
      return null;
    }
  }

  removePdf(): void {
    Swal.fire({
      title: 'Supprimer le PDF ?',
      text: 'Cette action ne peut pas être annulée.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.pdfFile = null;
        this.pdfFileName = null;
        this.uploadedPdfUrl = null;
        this.hasUnsavedChanges = true;
      }
    });
  }

  getPdfDisplayName(): string {
    if (this.pdfFileName) return this.pdfFileName;
    return '';
  }

  // ========== FORM SUBMISSION ==========
  private validateForm(): boolean {
    if (!this.magazineForm || this.magazineForm.invalid) {
      this.markFormGroupTouched(this.magazineForm);
      this.showError('Formulaire incomplet', 'Veuillez corriger les erreurs dans le formulaire');
      return false;
    }

    if (!this.imageFile) {
      this.showError('Image manquante', 'Veuillez sélectionner une image pour le magazine');
      return false;
    }

    // Validate PDF based on mode
    if (this.pdfInputMode === 'upload') {
      if (!this.pdfFile) {
        this.showError('PDF manquant', 'Veuillez sélectionner un fichier PDF ou entrer une URL');
        return false;
      }
    } else {
      const urlControl = this.pdfManualUrlControl;
      const url = urlControl?.value;
      
      if (!url || !url.trim()) {
        this.showError('URL manquante', 'Veuillez entrer une URL valide pour le PDF');
        return false;
      }
      
      // Basic URL validation regex
      const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      if (!urlPattern.test(url)) {
         this.showError('URL invalide', 'Veuillez entrer une URL HTTP/HTTPS valide');
         return false;
      }
    }

    return true;
  }

  async onSubmit(): Promise<void> {
    if (!this.validateForm()) return;

    if (this.isUploading || this.isUploadingPdf) {
      this.showWarning('Veuillez patienter', 'Un téléchargement est en cours');
      return;
    }

    this.isSubmitting = true;
    
    try {
      // 1. Upload image first
      const imageUrl = await this.uploadImageToAws();
      if (!imageUrl) {
        this.isSubmitting = false;
        return;
      }

      // 2. Handle PDF (Upload or Use URL)
      let finalPdfUrl: string | null = null;

      if (this.pdfInputMode === 'upload') {
        const pdfUrl = await this.uploadPdfToAws();
        if (!pdfUrl) {
          this.isSubmitting = false;
          return;
        }
        finalPdfUrl = pdfUrl;
      } else {
        // Use the manual URL directly
        finalPdfUrl = this.pdfManualUrlControl?.value;
      }

      // Show loading for backend creation
      Swal.fire({
        title: 'Création en cours...',
        text: 'Veuillez patienter pendant la création du magazine',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => Swal.showLoading()
      });

      // Prepare magazine data
      const magazineData = {
        name: this.nameControl?.value || '',
        download_link: finalPdfUrl,
        flip_book_link: finalPdfUrl,  // same URL for both
        nbr_telechargement: (this.nbrTelechargementControl?.value ?? 0).toString(),
        downloadCount: this.downloadCountControl?.value ?? 0,
        imageUrl: imageUrl
      };

      await this.createMagazine(magazineData);
      
    } catch (error) {
      console.error('Submission error:', error);
      Swal.close();
      this.showError('Erreur', 'Une erreur est survenue lors de la création du magazine');
      this.isSubmitting = false;
    }
  }

  private createMagazine(data: any): void {
    this.magazineService.createMagazine(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          Swal.close();
          this.showSuccess('Magazine créé avec succès');
          this.resetForm();
          this.router.navigate(['/listeMagazine']);
        },
        error: (error) => {
          Swal.close();
          console.error('Backend error:', error);
          this.showError('Erreur serveur', 'Impossible de créer le magazine. Veuillez réessayer.');
          this.isSubmitting = false;
        }
      });
  }

  resetForm(): void {
    if (!this.hasUnsavedChanges) {
      this.performReset();
      return;
    }

    Swal.fire({
      title: 'Réinitialiser le formulaire ?',
      text: 'Toutes les modifications seront perdues',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, réinitialiser',
      cancelButtonText: 'Annuler',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        this.performReset();
      }
    });
  }

  private performReset(): void {
    this.magazineForm?.reset({
      nbr_telechargement: 0,
      downloadCount: 0,
      pdfManualUrl: ''
    });
    
    // Reset Image
    this.imagePreview = null;
    this.imageFile = null;
    this.uploadedImageUrl = null;
    
    // Reset PDF
    this.pdfFile = null;
    this.pdfFileName = null;
    this.uploadedPdfUrl = null;
    this.pdfInputMode = 'upload'; // Reset to default mode
    
    this.hasUnsavedChanges = false;
    
    if (this.magazineForm) {
      Object.keys(this.formControls).forEach(key => {
        const control = this.formControls[key];
        control.markAsPristine();
        control.markAsUntouched();
      });
    }
  }

  navigateBack(): void {
    if (this.hasUnsavedChanges) {
      Swal.fire({
        title: 'Changements non sauvegardés',
        text: 'Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter ?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Quitter sans sauvegarder',
        cancelButtonText: 'Rester',
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          this.router.navigate(['/listeMagazine']);
        }
      });
    } else {
      this.router.navigate(['/listeMagazine']);
    }
  }

  // Helper methods
  private markFormGroupTouched(formGroup: FormGroup): void {
    if (!formGroup) return;
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  private showSuccess(message: string): void {
    Swal.fire({
      icon: 'success',
      title: 'Succès !',
      text: message,
      timer: 2000,
      showConfirmButton: false,
      background: '#f8f9fa',
      color: '#28a745'
    });
  }

  private showError(title: string, text: string): void {
    Swal.fire({
      icon: 'error',
      title: title,
      text: text,
      confirmButtonColor: '#dc3545',
      background: '#f8f9fa',
      color: '#dc3545'
    });
  }

  private showWarning(title: string, text: string): void {
    Swal.fire({
      icon: 'warning',
      title: title,
      text: text,
      confirmButtonColor: '#ffc107',
      background: '#f8f9fa'
    });
  }

  get isFormValid(): boolean {
    // Basic check, detailed check happens in validateForm()
    return this.magazineForm?.valid && !!this.imageFile;
  }

  get submitButtonText(): string {
    if (this.isSubmitting) return 'Création en cours...';
    return 'Créer le magazine';
  }
}